import React, { useEffect, useState, useCallback } from 'react';
import { MessageSquare, Send, Trash2, Plus, Loader2, AlertTriangle } from 'lucide-react';
import { suiteAPI } from '../../lib/api';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Textarea } from '../ui/textarea';
import { Panel, EmptyState, Card, Field, inputCls } from './shared';
import { toast } from 'sonner';

export default function SmsPanel({ setActiveSection }) {
  const [items, setItems] = useState([]);
  const [lists, setLists] = useState([]);
  const [integrations, setIntegrations] = useState(null);
  const [form, setForm] = useState({ name: '', body: '', list_ids: [] });
  const [busy, setBusy] = useState('');

  const load = useCallback(() => {
    suiteAPI.listSms().then(r => setItems(r.data));
    suiteAPI.getLists().then(r => setLists(r.data));
    suiteAPI.getIntegrations().then(r => setIntegrations(r.data));
  }, []);
  useEffect(() => { load(); }, [load]);

  const create = async () => {
    if (!form.body.trim()) return toast.error('Write your message');
    await suiteAPI.createSms(form);
    setForm({ name: '', body: '', list_ids: [] });
    load(); toast.success('SMS campaign saved');
  };

  const send = async (id, listIds) => {
    setBusy(id);
    try {
      const { data } = await suiteAPI.sendSms(id, { list_ids: listIds });
      toast.success(data.message);
      load();
    } catch (e) { toast.error(e.response?.data?.detail || 'Send failed'); }
    finally { setBusy(''); }
  };

  return (
    <Panel title="SMS Blasts" subtitle="Text campaigns sent through SignalWire. Build now, add credentials whenever you're ready — nothing is lost." testId="suite-sms">
      {integrations && !integrations.sms.configured && (
        <Card className="p-4 mb-5 flex items-start gap-3 border-amber-500/30">
          <AlertTriangle size={16} className="text-amber-400 mt-0.5 shrink-0" />
          <div>
            <p className="text-sm text-white font-medium">SignalWire is not connected yet</p>
            <p className="text-xs text-zinc-400">Messages you send will be queued as <span className="text-amber-400">pending configuration</span> until you add your Project ID, API token, space URL and sending number.</p>
            <Button size="sm" className="mt-2 bg-zinc-800 text-white text-xs" onClick={() => setActiveSection('integrations')} data-testid="sms-go-integrations">Open Integrations</Button>
          </div>
        </Card>
      )}

      <div className="grid lg:grid-cols-[400px_1fr] gap-5">
        <Card className="p-5 space-y-3 h-fit">
          <Field label="Campaign name"><Input value={form.name} onChange={e => setForm({ ...form, name: e.target.value })} className={inputCls} data-testid="sms-name" /></Field>
          <Field label="Message" hint={`${form.body.length}/1600 characters · include STOP instructions for compliance`}>
            <Textarea value={form.body} onChange={e => setForm({ ...form, body: e.target.value })} rows={4} placeholder="Drop is live! 200 jackets only: yourlink.co/x — reply STOP to opt out" className={inputCls} data-testid="sms-body" />
          </Field>
          <Field label="Lists">
            <div className="space-y-1.5">
              {lists.length === 0 && <p className="text-[11px] text-zinc-600">No lists yet — add contacts with phone numbers in Audience.</p>}
              {lists.map(l => (
                <label key={l.id} className="flex items-center gap-2 text-xs text-zinc-300">
                  <input type="checkbox" className="accent-[#b8ff00]" data-testid={`sms-list-${l.id}`}
                    checked={form.list_ids.includes(l.id)}
                    onChange={e => setForm(f => ({ ...f, list_ids: e.target.checked ? [...f.list_ids, l.id] : f.list_ids.filter(x => x !== l.id) }))} />
                  {l.name} <span className="text-zinc-600">({l.contact_count})</span>
                </label>
              ))}
            </div>
          </Field>
          <Button onClick={create} className="w-full bg-[#b8ff00] text-black font-bold" data-testid="sms-create"><Plus size={14} className="mr-1.5" /> Save campaign</Button>
        </Card>

        <div className="space-y-3">
          {items.length === 0 ? <EmptyState icon={MessageSquare} text="No SMS campaigns yet." /> : items.map(s => (
            <Card key={s.id} className="p-4" data-testid={`sms-item-${s.id}`}>
              <div className="flex items-start justify-between gap-3">
                <div className="min-w-0">
                  <p className="text-sm font-semibold text-white">{s.name || 'SMS Blast'}</p>
                  <p className="text-xs text-zinc-400 mt-1 whitespace-pre-wrap">{s.body}</p>
                  <p className="text-[11px] text-zinc-500 mt-1.5">{s.sent_count} sent · {s.status?.replace(/_/g, ' ')}</p>
                </div>
                <div className="flex gap-1 shrink-0">
                  <Button size="sm" disabled={busy === s.id} onClick={() => send(s.id, s.list_ids)} className="bg-zinc-800 text-white text-xs" data-testid={`sms-send-${s.id}`}>
                    {busy === s.id ? <Loader2 size={12} className="animate-spin" /> : <Send size={12} />}
                  </Button>
                  <Button size="sm" variant="ghost" className="text-zinc-500 hover:text-red-400" onClick={() => suiteAPI.deleteSms(s.id).then(load)}><Trash2 size={13} /></Button>
                </div>
              </div>
            </Card>
          ))}
        </div>
      </div>
    </Panel>
  );
}
